import { FileMimetype } from 'constants/misc'
import { logger } from 'utils/logging'

export interface ImageToDataURLOptions {
  /**
   * Callback called on successful conversion
   */
  onSuccess?: (dataURL: string) => void
  /**
   * Callback called on error
   */
  onError?: (error: Error) => void
  /**
   * Context for logging (component name, action, etc.)
   */
  logContext?: {
    component: string
    action: string
    metadata?: Record<string, unknown>
  }
  /**
   * Image format for data URL (default: 'image/png')
   */
  format?: string
}

/**
 * Converts an image URL to a data URL using canvas.
 * 
 * @param imageUrl - The URL of the image to convert
 * @param options - Optional callbacks and configuration
 * @returns Promise resolving to the data URL string
 * 
 * @example
 * ```ts
 * const dataURL = await imageToDataURL('https://example.com/image.jpg', {
 *   onSuccess: (url) => console.log('Converted!'),
 *   onError: (err) => console.error(err),
 *   logContext: { component: 'MyComponent', action: 'convertImage' }
 * })
 * ```
 */
export async function imageToDataURL(
  imageUrl: string,
  options?: ImageToDataURLOptions
): Promise<string> {
  const {
    onSuccess,
    onError,
    logContext,
    format = FileMimetype.IMAGE_JPEG,
  } = options || {}

  return new Promise<string>((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          const error = new Error('Could not get canvas context')

          if (logContext) {
            logger.error('Failed to get canvas context', error, {
              context: {
                ...logContext,
                metadata: {
                  ...logContext.metadata,
                  imageUrl
                }
              }
            })
          }

          onError?.(error)
          reject(error)
          return
        }

        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight
        ctx.drawImage(img, 0, 0)

        const dataURL = canvas.toDataURL(format)

        onSuccess?.(dataURL)
        resolve(dataURL)
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error))

        if (logContext) {
          logger.error('Error converting image to data URL', err, {
            context: {
              ...logContext,
              metadata: {
                ...logContext.metadata,
                imageUrl
              }
            }
          })
        }

        onError?.(err)
        reject(err)
      }
    }

    img.onerror = () => {
      const error = new Error('Failed to load image')

      if (logContext) {
        logger.error('Failed to load image', error, {
          context: {
            ...logContext,
            metadata: {
              ...logContext.metadata,
              imageUrl
            }
          }
        })
      }

      onError?.(error)
      reject(error)
    }

    img.src = imageUrl
  })
}
